/* eslint-disable react-hooks/exhaustive-deps */
import { FC, useEffect, useState } from "react";
import "./RetreatDetailScreen.css";
import eventShareIcon from "../../assets/images/event-share-button.svg";
import backIcon from "../../assets/images/back-icon.svg";
import calendarIcon from "../../assets/images/calendar-icon.svg";
import locationIcon from "../../assets/images/location-icon.svg";
import appTextIcon from "../../assets/images/header-text-icon.svg";
import whatsappIcon from "../../assets/images/whatsapp-icon.svg";
import wellnessStarIcon from "../../assets/images/wellness-orange-star.svg";
import fromDateArrowIcon from "../../assets/images/retreat-from-date-arrow.svg";
import clockIcon from "../../assets/images/retreat-clock-icon.svg";
import headerConnectIcon from "../../assets/images/dashboard-header-connect-icon.svg";
import shareIcon from "../../assets/images/reserved-share-icon.svg";
import tickIcon from "../../assets/images/reservation-tick-icon.svg";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ReserveAcknowledgementDialog from "components/elements/ReserveAcknowledgementDialog";
import ImageCarousel from "components/elements/ImageCarousel";
import {
  formatDateString,
  formatRetreatTime,
  getCalendarRealLifeImages,
  getTermaAndConditionsText,
  safeParse,
} from "utils/utils";
import {
  CALENDAR_RESERVATION_TABS,
  MENU_ITEM,
  PROFILE_STATUS,
  PURCHASE_TYPE,
} from "utils/constants";
import ROUTE_PATHS from "utils/routePaths";
import useWSMApi from "services/api/useWSMApi";
import { useMutation, useQuery } from "@tanstack/react-query";
import SnackbarAlert from "components/elements/SnackbarAlert";
import BecomeMemberDialog from "components/elements/BecomeMemberDialog";
import { STORAGE_KEY, STRING, WEB_LINK } from "utils/strings";
import { useLogout } from "services/LogoutProvider";
import { useStorage } from "services/StorageProvider";
import ConnectDialog from "components/elements/ConnectDialog";
import InformationPopup from "components/elements/InformationPopup";
import { useTabBar } from "services/TabBarProvider";
import CancelReservationPopup from "components/elements/CancelReservationPopup";
import {
  trackBackClicked,
  trackDetailView,
  trackEvent,
  trackReserveClick,
  trackSlotSelected,
} from "utils/analytics";
import ExpandableView from "components/elements/ExpandableView";

const RetreatDetailScreen: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { slotId } = useParams();
  const { setSelectedTabBar } = useTabBar();
  const { userProfileData } = useStorage();
  const { handleAppExit } = useLogout();
  const [selectedReservationData, setSelectedReservationData] =
    useState<any>(null);
  const [showAcknowledgementDialog, setShowAcknowledgementDialog] =
    useState<boolean>(false);
  const [showBecomeMemberDialog, setShowBecomeMemberDialog] =
    useState<boolean>(false);
  const [showConnectDialog, setShowConnectDialog] = useState<boolean>(false);
  const [showInformationPopup, setShowInformationPopup] =
    useState<boolean>(false);
  const [showReservationCancelPopup, setShowReservationCancelPopup] =
    useState<boolean>(false);
  const {
    reserveWSMSlot,
    cancelReservation,
    fetchWSMSlotById,
    fetchReservationBySlotId,
  } = useWSMApi();
  const [hasPurchaseAccess, setHasPurchaseAccess] = useState<boolean>(false);
  const [isActiveMember, setIsActiveMember] = useState<boolean>(false);
  const [isReserved, setIsReserved] = useState<boolean>(false);

  const { data: selectedSlotData } = useQuery({
    queryKey: ["get-slot-data", slotId],
    queryFn: () => fetchWSMSlotById(slotId ?? ""),
    enabled: !!slotId,
  });

  const { data: getReservationData } = useQuery({
    queryKey: ["get-reservation-data", slotId],
    queryFn: () => fetchReservationBySlotId(slotId ?? ""),
    enabled: !!userProfileData,
  });

  useEffect(() => {
    setIsActiveMember(
      userProfileData?.credit?.status === PROFILE_STATUS.MEMBER,
    );
  }, []);

  useEffect(() => {
    if (getReservationData) {
      setSelectedReservationData(getReservationData);
      setIsReserved(!getReservationData.is_cancelled);
    }
  }, [getReservationData]);

  useEffect(() => {
    sessionStorage.removeItem(STORAGE_KEY.WORKSPACE_DEEPLINK_DATA);

    // Parse reservation data
    const reservationData = safeParse(
      localStorage.getItem(STORAGE_KEY.SELECTED_RESERVATION_DATA),
    );
    if (reservationData) {
      setSelectedReservationData(reservationData);
      setIsReserved(!reservationData.is_cancelled);
    } else {
      setIsReserved(false);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem(
      STORAGE_KEY.SELECTED_SLOT_DATA,
      JSON.stringify(selectedSlotData),
    );
    setHasPurchaseAccess(
      isActiveMember ? true : (selectedSlotData?.non_member_access ?? false),
    );
    if (selectedSlotData?.id) {
      trackDetailView(
        String(selectedSlotData.id),
        selectedSlotData.service_type ?? "RETREAT",
      );
    }
  }, [selectedSlotData]);

  const slotReservationMutation = useMutation({
    mutationFn: () => reserveWSMSlot({ slot_id: selectedSlotData?.id }),
    onSuccess: () => {
      setShowAcknowledgementDialog(false);
      trackEvent("reservation_created", {
        slot_id: String(selectedSlotData?.id ?? ""),
        service_type: selectedSlotData?.service_type ?? "",
      });
      const paymentData = {
        title: `${selectedSlotData?.title}`,
        type: PURCHASE_TYPE.EVENT,
      };
      localStorage.setItem(
        STORAGE_KEY.PAYMENT_SUCCESS_DATA,
        JSON.stringify(paymentData),
      );
      navigate(ROUTE_PATHS.WSM_ALL_SET, { replace: true });
    },
  });

  const cancelReservationMutation = useMutation({
    mutationFn: () =>
      cancelReservation({
        slot_id: selectedSlotData?.id,
        reservation_id: selectedReservationData?.id,
      }),
    onSuccess: (response: any) => {
      setShowReservationCancelPopup(false);
      localStorage.setItem(
        STORAGE_KEY.RESERVATION_CANCEL_DATA,
        JSON.stringify(response),
      );
      navigate(ROUTE_PATHS.WSM_RESERVATION_CANCEL, { replace: true });
    },
  });

  const handleBackButtonClick = () => {
    trackBackClicked("RetreatDetail");
    if (location.key === "default" || !location.key) {
      navigate(ROUTE_PATHS.WSM_HOME, { replace: true });
    } else {
      navigate(-1);
    }
  };

  const handleReservationConfirmClick = () => {
    slotReservationMutation.mutate();
  };

  const openWhatsappChannel = () => {
    handleAppExit(WEB_LINK.WHATSAPP_CHANNEL);
  };

  const handleCancelReservationClick = () => {
    setShowReservationCancelPopup(true);
  };

  const handleCancelReservationConfirmClick = () => {
    cancelReservationMutation.mutate();
  };

  const handleRetreatReserveClick = () => {
    trackSlotSelected(
      selectedSlotData?.start_time ?? "",
      selectedSlotData?.slot_date ?? "",
    );
    trackReserveClick(String(selectedSlotData?.id ?? ""));
    if (userProfileData) {
      if (!hasPurchaseAccess || isReserved) return;
      if (isActiveMember) {
        const membershipEndDate = new Date(
          userProfileData?.credit?.membership_end_date,
        );
        const slotEndDate = new Date(selectedSlotData.end_time);

        if (slotEndDate > membershipEndDate && isActiveMember) {
          setShowInformationPopup(true);
          return;
        }
        setShowAcknowledgementDialog(true);
      } else {
        navigate(ROUTE_PATHS.WSM_CALENDAR_PAYMENT);
      }
    } else {
      if (slotId) {
        const deepLinkData = {
          id: slotId,
          type: selectedSlotData?.service_type,
        };
        sessionStorage.setItem(
          STORAGE_KEY.WORKSPACE_DEEPLINK_DATA,
          JSON.stringify(deepLinkData),
        );
        navigate(ROUTE_PATHS.WSM_SIGNIN, { replace: true });
      }
    }
  };

  const handleShareClick = async () => {
    const pageUrl = window.location.href;
    const shareText = `Founders don’t grow in noise.
Step into a better room at The Foundry:
`;
    try {
      if (navigator.share) {
        await navigator.share({
          url: slotId,
          text: shareText,
        });
      } else {
        await navigator.clipboard.writeText(pageUrl);
        alert("Copied to clipboard!");
      }
    } catch (err) {
      console.error("Share failed:", err);
    }
  };

  return (
    <div className="wms-mobile-container">
      {slotReservationMutation?.isError ? (
        <SnackbarAlert type={"error"}>
          {slotReservationMutation.error.message}
        </SnackbarAlert>
      ) : (
        ""
      )}
      <div className="retreat-detail-screen-container">
        <div className="retreat-detail-header-container">
          <div className="left-container">
            {userProfileData && (
              <img
                src={backIcon}
                alt="dropdown-logo"
                className="image"
                onClick={handleBackButtonClick}
              />
            )}
            <div className="header-title">
              {CALENDAR_RESERVATION_TABS.find(
                (type) => type.value === selectedSlotData?.service_type,
              )?.name || ""}
            </div>
          </div>

          <div
            className="right-container"
            onClick={() => setShowConnectDialog(true)}
          >
            <img src={headerConnectIcon} alt="left-button" />
            <span>Connect</span>
          </div>
        </div>
        {selectedSlotData?.slot_image && (
          <div
            className="retreat-detail-slot-image-container"
            style={{ backgroundImage: `url(${selectedSlotData?.slot_image})` }}
          >
            <div className="spots-container">
              <span>{Number(selectedSlotData?.total_seats || 0)}</span>
              <span style={{ fontSize: "13px" }}>Spots</span>
            </div>
          </div>
        )}
        <div className="retreat-detail-date-location-container">
          <div className="header-container">
            <div className="header-title">
              <span className="header-title-primary">
                {selectedSlotData?.title}
              </span>
              {selectedSlotData?.pod_types?.includes("ONE_SEATER") &&
              selectedSlotData?.service_type !== "MEETING_ROOMS" ? (
                <span className="header-title-secondary"> - Seat Only</span>
              ) : null}
            </div>
            <img
              src={eventShareIcon}
              alt="share-event-icon"
              className="share-image"
              onClick={handleShareClick}
            />
          </div>

          <div className="detail-container">
            <div className="image">
              <img src={calendarIcon} alt="calendar-logo" />
            </div>
            <div className="date-time-container">
              <div>
                <div className="title">
                  {formatDateString(selectedSlotData?.start_time)}
                </div>
                <div className="date-time">
                  <img src={clockIcon} alt="retreat-clock-icon" />
                  {formatRetreatTime(selectedSlotData?.start_time)}
                </div>
              </div>
              <img src={fromDateArrowIcon} alt="from-date-arrow-logo" />
              <div>
                <div className="title">
                  {formatDateString(selectedSlotData?.end_time)}
                </div>
                <div className="date-time">
                  <img src={clockIcon} alt="retreat-clock-icon" />
                  {formatRetreatTime(selectedSlotData?.end_time)}
                </div>
              </div>
            </div>
          </div>
          <div className="divider"></div>
          <div className="detail-container">
            <div className="image">
              <img src={locationIcon} alt="location-logo" />
            </div>
            <div className="right-container">
              <div>
                <div className="title">{selectedSlotData?.location}</div>
                {selectedSlotData?.sub_location && (
                  <div className="date-time">
                    {selectedSlotData?.sub_location}
                  </div>
                )}
              </div>
              <button
                onClick={() => handleAppExit(selectedSlotData?.location_url)}
              >
                Direction
              </button>
            </div>
          </div>
        </div>
        {selectedSlotData?.description && (
          <div className="retreat-detail-container">
            <div className="header-bar-container">
              <span className="detail-title">
                bout the <span style={{ color: "#C2B58C" }}>retreat</span>
              </span>
              {selectedSlotData?.landing_url && (
                <button
                  onClick={() => {
                    navigate(ROUTE_PATHS.WSM_WEB_LINK_DETAIL, {
                      state: {
                        anyData: {
                          title:
                            CALENDAR_RESERVATION_TABS.find(
                              (type) =>
                                type.value === selectedSlotData?.service_type,
                            )?.name || "",
                          url: selectedSlotData?.landing_url,
                        },
                      },
                    });
                  }}
                >
                  Learn more
                </button>
              )}
            </div>
            <div className="divider"></div>
            <div
              className="sub-title"
              dangerouslySetInnerHTML={{
                __html: selectedSlotData?.description || "",
              }}
            ></div>
          </div>
        )}
        <div className="retreat-detail-contact-detail-container">
          <ImageCarousel
            images={
              selectedSlotData?.real_life_images?.length > 0
                ? selectedSlotData?.real_life_images
                : getCalendarRealLifeImages(selectedSlotData?.service_type)
            }
            title="In Real"
            subTitle="Life"
          />
        </div>
        <div className="retreat-detail-contact-detail-container">
          {selectedSlotData?.instructor && (
            <div>
              <span className="header-title">
                ur <span style={{ color: "#C2B58C" }}>Guest</span>
              </span>
              <div className="divider"></div>
              <div className="workspace-instructor-container">
                <div className="image-container">
                  <img
                    src={selectedSlotData?.instructor?.profile_pic}
                    alt="calendar-logo"
                  />
                </div>
                <div className="text-container">
                  <div className="header">
                    <span className="title">
                      {selectedSlotData?.instructor?.first_name}
                    </span>
                    <span className="sub-title">
                      {" "}
                      {selectedSlotData?.instructor?.last_name}
                    </span>
                  </div>
                  {selectedSlotData?.instructor?.about
                    ?.split(",")
                    .map((about: string, index: number) => (
                      <div className="details" key={index}>
                        <img
                          src={wellnessStarIcon}
                          className="image"
                          alt="workspace-start-icon"
                        />
                        <span>{about.trim()}</span>
                      </div>
                    ))}
                </div>
              </div>
              <div className="divider"></div>
            </div>
          )}

          <div className="footer-container">
            <div className="title">Contact Details</div>
            <div className="sub-container">
              <img src={appTextIcon} alt="sabe-text-logo" />
              <img
                src={whatsappIcon}
                alt="whatsapp-logo"
                onClick={openWhatsappChannel}
                style={{ cursor: "pointer" }}
              />
            </div>
          </div>
        </div>
        <ExpandableView title="Terms &" subTitle="Conditions">
          <div className="retreat-detail-expanded-container">
            <div className="divider"></div>
            <div className="sub-container">
              <div className="sub-title">
                {getTermaAndConditionsText(selectedSlotData?.service_type)}
              </div>
            </div>
          </div>
        </ExpandableView>
        {selectedReservationData && selectedReservationData?.is_cancelled ? (
          <div className="retreat-detail-screen-footer-container">
            <div
              className="button-container"
              style={{
                backgroundColor: "darkgray",
                cursor: "not-allowed",
              }}
            >
              <div className="left-container">Cancelled</div>
            </div>
          </div>
        ) : isReserved ? (
          <>
            <div className="retreat-detail-screen-footer-container">
              <div className="reserved-parent-container">
                <div className="reserved-button-container">
                  <img src={tickIcon} alt="reserved-tick-button" />
                  Reserved
                </div>
                <div
                  className="share-button-container"
                  onClick={handleShareClick}
                >
                  <img src={shareIcon} alt="reserved-share-button" />
                  Share
                </div>
              </div>
            </div>
            <div
              className="retreat-detail-screen-cancel-reservation-container"
              onClick={handleCancelReservationClick}
            >
              Cancel
            </div>
          </>
        ) : (
          <div className="retreat-detail-screen-footer-container">
            <div
              className="button-container"
              onClick={handleRetreatReserveClick}
              style={{
                backgroundColor:
                  hasPurchaseAccess && !isReserved ? "white" : "darkgray",
                cursor:
                  hasPurchaseAccess && !isReserved ? "pointer" : "not-allowed",
              }}
            >
              <div className="left-container">
                {isReserved ? "Reserved" : "Reserve Now"}
              </div>
              {!isReserved && (
                <div className="right-container">
                  <div className="divider"></div>
                  <div className="text-container">
                    <span>Exclusive</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <ReserveAcknowledgementDialog
        open={showAcknowledgementDialog}
        onClose={() => setShowAcknowledgementDialog(false)}
        slotData={selectedSlotData}
        onClickLetsDo={handleReservationConfirmClick}
        isLoading={slotReservationMutation?.isPending}
      />
      <BecomeMemberDialog
        profileStatus={userProfileData?.credit?.status}
        open={showBecomeMemberDialog}
        onClose={() => setShowBecomeMemberDialog(false)}
      />
      <ConnectDialog
        open={showConnectDialog}
        onClose={() => setShowConnectDialog(false)}
      />
      <InformationPopup
        open={showInformationPopup}
        onClose={() => setShowInformationPopup(false)}
        onClick={() => {
          setShowInformationPopup(false);
          navigate(-1);
          setSelectedTabBar(MENU_ITEM.MEMBERSHIP);
        }}
        title={STRING.MEMBERSHIP_EXPIRE_INFO_TITLE}
        subTitle={STRING.MEMBERSHIP_EXPIRE_INFO_SUB_TITLE}
        buttonText={STRING.MEMBERSHIP_EXPIRE_INFO_BUTTON_TITLE}
      />
      <CancelReservationPopup
        open={showReservationCancelPopup}
        reservationData={selectedReservationData}
        onClose={() => setShowReservationCancelPopup(false)}
        onClick={handleCancelReservationConfirmClick}
      />
    </div>
  );
};

export default RetreatDetailScreen;
